import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  computed,
  DestroyRef,
  inject,
  signal,
} from '@angular/core';
import { Ripple } from 'primeng/ripple';
import { StyleClassModule } from 'primeng/styleclass';
import { NavigationEnd, Router, RouterLink, RouterLinkActive } from '@angular/router';
import { MenuItem } from '@/layouts/sidebar/menu.model';
import { MENU } from '@/layouts/sidebar/menu';
import { filter, merge, of, switchMap } from 'rxjs';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { NgIcon, provideIcons } from '@ng-icons/core';
import { bootstrapArchive } from '@ng-icons/bootstrap-icons';
import { BadgeModule } from 'primeng/badge';
import { ButtonModule } from 'primeng/button';
import { DataRequestService } from '@/features/data-requests/data-request.service';
import { DataRequestStatus } from '@shared/models/data-requests';
import { map, skip } from 'rxjs/operators';
import { ModuleGroup } from '@shared/models/module-group.enum';
import { UserService } from '@core/services';
import { Module, Permission } from '@shared/models';
import { PersonalizationService } from '@shared/services/personalization.service';
import { TranslatePipe } from '@ngx-translate/core';
import { LayoutService } from '@/shared/services/ui';

@Component({
  selector: 'cis-sidebar',
  imports: [Ripple, StyleClassModule, RouterLink, RouterLinkActive, NgIcon, BadgeModule, TranslatePipe, ButtonModule],
  viewProviders: [provideIcons({ bootstrapArchive })],
  templateUrl: './sidebar.component.html',
  styleUrls: ['./sidebar.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SidebarComponent {
  private readonly personalizationService = inject(PersonalizationService);
  private readonly userService = inject(UserService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly cdRef = inject(ChangeDetectorRef);
  private readonly dataRequestService = inject(DataRequestService);
  private readonly layoutService = inject(LayoutService);

  router = inject(Router);

  isCondensed = computed(() => !this.layoutService.sidebarVisible());

  constructor() {
    this.router.events
      .pipe(
        filter((e) => e instanceof NavigationEnd),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(() => {
        this.cdRef.detectChanges();
      });
  }

  readableModulePermissions = toSignal(this.userService.getReadableModules(), {
    initialValue: {},
  });

  currentProfile = toSignal(this.personalizationService.profiles$, {
    initialValue: null,
  });

  dataRequestsCount = toSignal(
    merge(this.dataRequestService.refresher$, this.personalizationService.profiles$).pipe(
      skip(1),
      switchMap(() => {
        if (this.userService.hasPermission(Module.DataRequest, Permission.Read)) {
          return this.dataRequestService
            .getByQuery({
              pageSize: 0,
              pageNumber: 1,
              searchTerm: '',
              statusIds: [
                DataRequestStatus.CorrectionRequired,
                DataRequestStatus.InReview,
                DataRequestStatus.InProgress,
                DataRequestStatus.NotStarted,
              ],
            }, 0)
            .pipe(map((t) => t.data.totalRecords));
        }
        return of(0);
      }),
    ),
    { initialValue: 0 },
  );

  hoveredId = signal(-999);

  hasActiveChild(item: MenuItem) {
    return item.children?.some((child) => this.router.url.startsWith(child.routerUrl ?? ''));
  }

  isActive(item: MenuItem) {
    return this.router.url.startsWith(item.routerUrl ?? '');
  }

  getBadgeValue(badgeName: string) {
    switch (badgeName) {
      case 'dataRequests':
        return this.dataRequestsCount();
      default:
        return 0;
    }
  }

  hasGroup({ groupId }: MenuItem) {
    if ((groupId as number) === 0) {
      return true;
    }
    const readablePermissions = this.readableModulePermissions();

    if (Object.hasOwn(readablePermissions, groupId!)) {
      return true;
    }
    return false;
  }

  hasGroupChild(groupId: ModuleGroup, moduleId: Module | number) {
    if (groupId === ModuleGroup.Report) {
      if (moduleId === ModuleGroup.QuestionnaireReport) {
        return (this.readableModulePermissions()[ModuleGroup.QuestionnaireReport] ?? []).length > 0;
      }
      return (this.readableModulePermissions()[ModuleGroup.Report] ?? []).length > 0;
    }

    if (groupId === ModuleGroup.Administration && moduleId === -1) {
      if (this.readableModulePermissions()[ModuleGroup.Setup]?.length) {
        return this.readableModulePermissions()[ModuleGroup.Setup].filter((m: any) => m.permissions.length > 0);
      }
    }

    const profile = this.currentProfile();
    if (groupId === ModuleGroup.Administration && profile?.name.toLowerCase().includes('super admin')) {
      return true;
    }

    if (this.readableModulePermissions()[groupId]) {
      return this.readableModulePermissions()[groupId].filter((m: any) => m.module.id === moduleId).length > 0;
    }

    return false;
  }

  protected readonly MENU = MENU;

  expandSidebar(event: Event) {
    event.preventDefault();
    if (!this.layoutService.sidebarVisible()) {
      this.layoutService.sidebarVisible.set(true);
    }
  }
}
